import { useState, useEffect } from 'react';
import { transactionsAPI, accountsAPI } from '../lib/api';
import { formatCurrency, formatDateTime } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  List, ArrowUpRight, ArrowDownRight, Clock, ArrowLeftRight,
  Receipt, Send, Landmark, Globe, ShieldCheck, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

export default function TransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAccounts();
  }, []);

  useEffect(() => {
    loadTransactions();
  }, [selectedAccount, selectedType]);

  const loadAccounts = async () => {
    try {
      const res = await accountsAPI.list();
      setAccounts(res.data.accounts);
    } catch (err) {
      console.error('Failed to load accounts');
    }
  };

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedAccount !== 'all') params.account_id = selectedAccount;
      if (selectedType !== 'all') params.type = selectedType;
      const res = await transactionsAPI.list(params);
      setTransactions(res.data.transactions);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'deposit': return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'hold': return <ArrowUpRight className="h-4 w-4 text-amber-500" />;
      case 'transfer_in': return <ArrowDownRight className="h-4 w-4 text-green-500" />;
      case 'international_transfer': return <Globe className="h-4 w-4 text-blue-500" />;
      case 'bill_payment': return <Receipt className="h-4 w-4 text-orange-500" />;
      case 'zelle': return <Send className="h-4 w-4 text-purple-500" />;
      case 'wire_transfer': return <Landmark className="h-4 w-4 text-blue-600" />;
      case 'external_transfer': return <ArrowUpRight className="h-4 w-4 text-cyan-500" />;
      default: return <ArrowLeftRight className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeBadge = (type) => {
    const colors = {
      deposit: 'bg-green-50 text-green-700',
      hold: 'bg-amber-50 text-amber-700',
      transfer_in: 'bg-green-50 text-green-700',
      international_transfer: 'bg-blue-50 text-blue-700',
      bill_payment: 'bg-orange-50 text-orange-700',
      zelle: 'bg-purple-50 text-purple-700',
      wire_transfer: 'bg-indigo-50 text-indigo-700',
      external_transfer: 'bg-cyan-50 text-cyan-700',
    };
    const labels = {
      deposit: 'Deposit',
      hold: 'Hold/Pending',
      transfer_in: 'Transfer In',
      international_transfer: 'International',
      bill_payment: 'Bill Payment',
      zelle: 'Zelle',
      wire_transfer: 'Wire Transfer',
      external_transfer: 'External Transfer',
    };
    return <Badge className={`text-[10px] ${colors[type] || 'bg-gray-50 text-gray-700'}`}>{labels[type] || type}</Badge>;
  };

  return (
    <div className="page-container">
      <h2 className="text-2xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Space Grotesk' }}>Transaction History</h2>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select value={selectedAccount} onValueChange={setSelectedAccount} data-testid="transactions-filter-account">
          <SelectTrigger className="w-full sm:w-64">
            <SelectValue placeholder="All Accounts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map(a => (
              <SelectItem key={a._id} value={a._id}>{a.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="deposit">Deposits</SelectItem>
            <SelectItem value="hold">Hold/Pending</SelectItem>
            <SelectItem value="transfer_in">Transfers In</SelectItem>
            <SelectItem value="international_transfer">International</SelectItem>
            <SelectItem value="bill_payment">Bill Payments</SelectItem>
            <SelectItem value="zelle">Zelle</SelectItem>
            <SelectItem value="wire_transfer">Wire Transfers</SelectItem>
            <SelectItem value="external_transfer">External Transfers</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Transactions Table */}
      <Card data-testid="transactions-table">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <div className="skeleton-pulse w-8 h-8 bg-muted rounded-full mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="p-8 text-center" data-testid="transactions-empty-state">
              <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-base font-medium">No transactions found</p>
              <p className="text-sm text-muted-foreground mt-1">Your transaction history will appear here</p>
              <Button variant="outline" className="mt-4" onClick={() => window.location.href = '/transfer'}>Make a Transfer</Button>
            </div>
          ) : (
            <div className="divide-y">
              {transactions.map(tx => (
                <div key={tx._id} className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors" data-testid="transactions-row">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                      {getTypeIcon(tx.type)}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{tx.description}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <p className="text-xs text-muted-foreground">{formatDateTime(tx.date)}</p>
                        {getTypeBadge(tx.type)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-semibold tabular-nums ${tx.amount >= 0 ? 'amount-positive' : 'amount-negative'}`}>
                      {tx.amount >= 0 ? '+' : ''}{formatCurrency(Math.abs(tx.amount))}
                    </p>
                    {tx.balance_after !== undefined && (
                      <p className="text-xs text-muted-foreground">Bal: {formatCurrency(tx.balance_after)}</p>
                    )}
                    {tx.converted_amount && tx.target_currency && (
                      <p className="text-xs text-blue-600">
                        {formatCurrency(tx.converted_amount)} {tx.target_currency}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
